import { isInactiveAppointmentStatus } from './appointmentStatusUtils';
import { parseDurationFromBackend } from './durationUtils';

export const SERVICE_TYPE_PRIMARY = 'Primary';
export const SERVICE_TYPE_SECONDARY = 'Secondary';

const SLOT_INTERVAL_MINUTES = {
  Five: 5,
  Ten: 10,
  Fifteen: 15,
  Thirty: 30,
  Sixty: 60,
};

export const normalizeBookingServiceType = (type) => {
  if (type === 2 || `${type}`.toLowerCase() === SERVICE_TYPE_SECONDARY.toLowerCase()) {
    return SERVICE_TYPE_SECONDARY;
  }

  return SERVICE_TYPE_PRIMARY;
};

export const isPrimaryBookingService = (service) => normalizeBookingServiceType(service?.type) === SERVICE_TYPE_PRIMARY;
export const isStandaloneBookingService = (service) => isPrimaryBookingService(service) || Boolean(service?.isStandalone);

export const isNonStandaloneSecondaryBookingService = (service) => (
  normalizeBookingServiceType(service?.type) === SERVICE_TYPE_SECONDARY && !Boolean(service?.isStandalone)
);

export const getBookingServicePriority = (service) => {
  if (isPrimaryBookingService(service)) {
    return 0;
  }

  return isStandaloneBookingService(service) ? 1 : 2;
};

export const sortServicesForBooking = (services) => [...services].sort(
  (left, right) => getBookingServicePriority(left) - getBookingServicePriority(right),
);

export const memberCanPerformBookingService = (member, service) => {
  if (!service) {
    return false;
  }

  if (service.isCombo) {
    const serviceIds = service.serviceIds || [];
    if (serviceIds.length < 2) {
      return false;
    }

    const memberServiceIds = new Set((member?.services || []).map((item) => item.id));
    return serviceIds.every((serviceId) => memberServiceIds.has(serviceId));
  }

  return (member?.services || []).some((item) => item.id === service.id);
};

export const getTeamMembersWhoCanDoService = (teamMembers, serviceId) => (
  (teamMembers || []).filter((member) => member.services?.some((service) => service.id === serviceId))
);

export const getTeamMembersWhoCanDoAllServices = (teamMembers, serviceIds, servicesById = null) => (
  (teamMembers || []).filter((member) => serviceIds.every((serviceId) => {
    const service = servicesById?.get?.(serviceId);
    if (service?.isCombo) {
      return memberCanPerformBookingService(member, service);
    }

    return (member.services || []).some((item) => item.id === serviceId);
  }))
);

export function computeSuggestedAssignment(teamMembers, serviceIds, servicesById = null) {
  const canDoAll = getTeamMembersWhoCanDoAllServices(teamMembers, serviceIds, servicesById);
  if (canDoAll.length === 1) {
    const one = canDoAll[0];
    return Object.fromEntries(serviceIds.map((serviceId) => [serviceId, one.id]));
  }

  const assignment = {};
  serviceIds.forEach((serviceId) => {
    const candidates = getTeamMembersWhoCanDoService(teamMembers, serviceId);
    if (candidates.length === 1) {
      assignment[serviceId] = candidates[0].id;
    }
  });
  return assignment;
}

export const parseDurationToMinutes = (duration) => {
  if (typeof duration === 'number' && Number.isFinite(duration)) {
    return duration;
  }

  if (typeof duration !== 'string' || !duration) {
    return 30;
  }

  const parsed = parseDurationFromBackend(duration);
  if (Number.isFinite(parsed)) {
    return parsed;
  }

  const normalizedDuration = duration.includes('h') ? duration.replace('h', ':') : duration;
  const parts = normalizedDuration.split(':');
  const hours = Number.parseInt(parts[0], 10) || 0;
  const minutes = Number.parseInt(parts[1], 10) || 0;
  return (hours * 60) + minutes;
};

export const parseTimeToMinutes = (timeValue) => {
  if (typeof timeValue !== 'string' || !timeValue) {
    return 0;
  }

  const [hours, minutes] = timeValue.split(':').map((value) => Number.parseInt(value, 10) || 0);
  return (hours * 60) + minutes;
};

export const formatMinutesToTime = (totalMinutes) => {
  const normalizedMinutes = ((totalMinutes % (24 * 60)) + (24 * 60)) % (24 * 60);
  const hours = Math.floor(normalizedMinutes / 60);
  const minutes = normalizedMinutes % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

export const parseSlotIntervalMinutes = (slotInterval) => SLOT_INTERVAL_MINUTES[slotInterval] || 30;

// Staff book and edit on a fixed 5-minute grid. The store slot interval only shapes the customer calendar.
export const MANAGEMENT_START_TIME_INTERVAL_MINUTES = 5;

export const buildManagementStartTimeOptions = () => {
  const options = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += MANAGEMENT_START_TIME_INTERVAL_MINUTES) {
    options.push(formatMinutesToTime(minutes));
  }
  return options;
};

export const MANAGEMENT_START_TIME_OPTIONS = buildManagementStartTimeOptions();

const WEEKDAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const getWeekdayHours = (days, dateInput) => {
  if (!days?.length || !dateInput) {
    return null;
  }

  const date = typeof dateInput === 'string'
    ? new Date(`${dateInput}T12:00:00`)
    : dateInput;
  const weekday = WEEKDAY_ORDER[date.getDay() === 0 ? 6 : date.getDay() - 1];
  return days.find((entry) => entry.weekDay === weekday) || null;
};

export const getOpeningHoursForStoreDate = (store, dateInput) => getWeekdayHours(store?.openingHours, dateInput);

export const buildWorkingHoursByTeamMemberId = (entries) => Object.fromEntries(
  (entries || [])
    .map((entry) => [String(entry.teamMemberId ?? entry.TeamMemberId ?? entry.id), entry.workingHours ?? entry.WorkingHours])
    .filter(([id, workingHours]) => id !== 'undefined' && Array.isArray(workingHours)),
);

const getPeriodsWarning = (periods, startMinutes, endMinutes) => {
  if (!periods?.length) {
    return 'closedDay';
  }

  const isWithinPeriods = periods.some((period) => {
    const from = parseTimeToMinutes(period.from);
    const to = parseTimeToMinutes(period.to);
    return startMinutes >= from && endMinutes <= to;
  });

  return isWithinPeriods ? null : 'outsideHours';
};

export const getManagementBookingHoursWarning = (store, date, startTime, endTimeMinutes) => {
  if (!store || !date || !startTime) {
    return null;
  }

  const startMinutes = parseTimeToMinutes(startTime);
  return getPeriodsWarning(
    getOpeningHoursForStoreDate(store, date)?.periods,
    startMinutes,
    endTimeMinutes ?? startMinutes,
  );
};

export const isCancelledAppointmentStatus = (status) => isInactiveAppointmentStatus(status);

export const timeRangesOverlap = (startA, endA, startB, endB) => startA < endB && startB < endA;

const buildProposedTeamMemberPeriods = (
  selectedServices,
  serviceProfessionalMap,
  startTime,
  prefilledTeamMemberId,
) => {
  if (!startTime) {
    return [];
  }

  if (selectedServices.length > 0) {
    const assignments = buildPerServiceAssignments(
      selectedServices,
      serviceProfessionalMap,
      startTime,
    );

    return assignments.map((assignment) => {
      const service = selectedServices.find((item) => item.id === assignment.serviceId);
      const from = parseTimeToMinutes(assignment.startTime);
      const to = from + parseDurationToMinutes(service?.duration);

      return {
        teamMemberId: assignment.teamMemberId,
        from,
        to,
      };
    });
  }

  if (!prefilledTeamMemberId) {
    return [];
  }

  const from = parseTimeToMinutes(startTime);
  return [{
    teamMemberId: prefilledTeamMemberId,
    from,
    to: from + 1,
  }];
};

export const getManagementBookingConflictWarning = ({
  date,
  startTime,
  selectedServices,
  serviceProfessionalMap,
  existingAppointments,
  excludeAppointmentId,
  prefilledTeamMemberId,
}) => {
  if (!date || !startTime || !existingAppointments?.length) {
    return null;
  }

  const proposedPeriods = buildProposedTeamMemberPeriods(
    selectedServices,
    serviceProfessionalMap,
    startTime,
    prefilledTeamMemberId,
  );

  if (proposedPeriods.length === 0) {
    return null;
  }

  const blockingAppointments = existingAppointments.filter((appointment) => {
    if (excludeAppointmentId && appointment.id === excludeAppointmentId) {
      return false;
    }

    if (appointment.date !== date) {
      return false;
    }

    return !isCancelledAppointmentStatus(appointment.status);
  });

  for (const proposedPeriod of proposedPeriods) {
    for (const appointment of blockingAppointments) {
      for (const service of appointment.services || []) {
        if (String(service.teamMemberId) !== String(proposedPeriod.teamMemberId)) {
          continue;
        }

        const existingFrom = parseTimeToMinutes(service.period?.from);
        const existingTo = parseTimeToMinutes(service.period?.to);

        if (timeRangesOverlap(proposedPeriod.from, proposedPeriod.to, existingFrom, existingTo)) {
          return 'slotConflict';
        }
      }
    }
  }

  return null;
};

export const computeManagementBookingEndMinutes = (
  selectedServices,
  serviceProfessionalMap,
  startTime,
) => {
  let endMinutes = parseTimeToMinutes(startTime);

  if (selectedServices.length === 0) {
    return endMinutes;
  }

  const assignments = buildPerServiceAssignments(
    selectedServices,
    serviceProfessionalMap,
    startTime,
  );

  if (assignments.length > 0) {
    const lastAssignment = assignments[assignments.length - 1];
    const lastService = selectedServices.find((service) => service.id === lastAssignment.serviceId);
    endMinutes = parseTimeToMinutes(lastAssignment.startTime) + parseDurationToMinutes(lastService?.duration);
  } else {
    endMinutes += selectedServices.reduce(
      (total, service) => total + parseDurationToMinutes(service.duration),
      0,
    );
  }

  return endMinutes;
};

export const getManagementBookingWarning = ({
  store,
  date,
  startTime,
  selectedServices,
  serviceProfessionalMap,
  existingAppointments,
  excludeAppointmentId,
  prefilledTeamMemberId,
  workingHoursByTeamMemberId,
}) => {
  const conflictWarning = getManagementBookingConflictWarning({
    date,
    startTime,
    selectedServices,
    serviceProfessionalMap,
    existingAppointments,
    excludeAppointmentId,
    prefilledTeamMemberId,
  });

  if (conflictWarning) {
    return conflictWarning;
  }

  const endMinutes = computeManagementBookingEndMinutes(
    selectedServices,
    serviceProfessionalMap,
    startTime,
  );

  const proposedPeriods = buildProposedTeamMemberPeriods(
    selectedServices,
    serviceProfessionalMap,
    startTime,
    prefilledTeamMemberId,
  );
  const hasProfessionalWithOwnHours = proposedPeriods.some(
    (period) => workingHoursByTeamMemberId?.[String(period.teamMemberId)],
  );
  if (!store || !date || !startTime || !hasProfessionalWithOwnHours) {
    return getManagementBookingHoursWarning(store, date, startTime, endMinutes);
  }

  for (const period of proposedPeriods) {
    const ownHours = workingHoursByTeamMemberId[String(period.teamMemberId)];
    const periods = getWeekdayHours(ownHours || store.openingHours, date)?.periods;
    const warning = getPeriodsWarning(periods, period.from, period.to);
    if (warning) {
      return warning;
    }
  }

  return null;
};

export const snapMinutesToSlot = (minutes, slotIntervalMinutes) => (
  Math.round(minutes / slotIntervalMinutes) * slotIntervalMinutes
);

export const OPEN_PROFESSIONAL_TEAM_MEMBER_ID = '00000000-0000-0000-0000-000000000000';

export const buildSequentialAssignments = (servicesForSchedule, teamMemberId, groupStartTime) => {
  if (!teamMemberId || !groupStartTime) {
    return [];
  }

  let currentStartMinute = parseTimeToMinutes(groupStartTime);

  return servicesForSchedule.map((service) => {
    const assignment = {
      serviceId: service.isCombo ? null : service.id,
      serviceComboId: service.isCombo ? service.id : null,
      teamMemberId,
      startTime: formatMinutesToTime(currentStartMinute),
    };

    currentStartMinute += parseDurationToMinutes(service.duration);
    return assignment;
  });
};

export const buildOpenProfessionalAssignments = (servicesForSchedule, groupStartTime) => (
  buildSequentialAssignments(servicesForSchedule, OPEN_PROFESSIONAL_TEAM_MEMBER_ID, groupStartTime)
);

export const applyLineDurations = (services, quotes) => (services || []).map((service) => {
  const minutes = Number(quotes?.[service.id]?.durationMinutes);
  if (!service || service.isCombo || !Number.isFinite(minutes) || minutes <= 0) return service;
  return { ...service, duration: minutes };
});

export const withLineQuotes = (assignments, quotes, canSetPrice) => (assignments || []).map((assignment) => {
  if (!assignment?.serviceId || assignment.serviceComboId) return assignment;
  const quote = quotes?.[assignment.serviceId];
  if (!quote) return assignment;
  const next = { ...assignment };
  const minutes = Number(quote.durationMinutes);
  if (Number.isFinite(minutes) && minutes > 0) {
    const hours = Math.floor(minutes / 60);
    const remaining = minutes % 60;
    next.duration = `${String(hours).padStart(2, '0')}:${String(remaining).padStart(2, '0')}`;
  }
  if (
    canSetPrice
    && quote.priceTouched
    && quote.unitPrice !== ''
    && quote.unitPrice != null
    && Number.isFinite(Number(quote.unitPrice))
  ) {
    next.unitPrice = Number(quote.unitPrice);
  }
  return next;
});

export const buildPerServiceAssignments = (orderedServices, serviceProfessionalMap, groupStartTime) => {
  if (!groupStartTime || orderedServices.length === 0) {
    return [];
  }

  const groups = [];
  orderedServices.forEach((service) => {
    const teamMemberId = serviceProfessionalMap[service.id];
    if (!teamMemberId) {
      return;
    }

    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.teamMemberId === teamMemberId) {
      lastGroup.services.push(service);
      return;
    }

    groups.push({ teamMemberId, services: [service] });
  });

  let currentStartMinute = parseTimeToMinutes(groupStartTime);

  return groups.flatMap((group) => {
    const assignments = group.services.map((service) => {
      const assignment = {
        serviceId: service.isCombo ? null : service.id,
        serviceComboId: service.isCombo ? service.id : null,
        teamMemberId: group.teamMemberId,
        startTime: formatMinutesToTime(currentStartMinute),
      };
      currentStartMinute += parseDurationToMinutes(service.duration);
      return assignment;
    });
    return assignments;
  });
};

export const applyAppointmentServicePrice = (appointment, serviceId, unitPrice) => {
  const price = Number(unitPrice);
  const services = (appointment?.services || []).map((service) => (
    Number(service.serviceId) === Number(serviceId) && service.unitPrice == null
      ? { ...service, unitPrice: price }
      : service
  ));
  const uncovered = services.reduce((sum, service) => {
    if (service.coveredByMembership || service.coveredByPackage || service.unitPrice == null) return sum;
    return sum + Number(service.unitPrice);
  }, 0);
  return {
    ...appointment,
    services,
    hasUnpricedServices: services.some((service) => service.unitPrice == null),
    totalPrice: services.reduce((sum, service) => sum + (service.unitPrice == null ? 0 : Number(service.unitPrice)), 0),
    amountDue: uncovered + (Number(appointment?.additionalCostAmount) || 0),
  };
};
